import { useEffect, useState, type ReactNode } from 'react';
import { Camera, Package, Stethoscope } from 'lucide-react';
import { ActPhotoGallery } from './camera/PhotoGallery';
import type { ActPhoto } from './camera/types';
import { listActPhotos } from '../services/actPhotosApi';

const actStatusLabels: Record<string, string> = {
  PLANNED: 'Planifié',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

const actStatusBadgeClasses: Record<string, string> = {
  PLANNED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-700',
};

export interface AppointmentActTraceView {
  id: string;
  sequence?: number | null;
  status: string;
  serviceName?: string | null;
  notes?: string | null;
  performedAt?: string | null;
  demandDetails?: Record<string, any> | null;
  piercingRecord?: {
    needleGauge?: string | null;
    jewelry?: unknown;
    placement?: unknown;
    piercingType?: string | null;
    bodyZone?: { id: string; name: string } | null;
    piercingTypeModel?: { id: string; name: string; basePrice?: number | null } | null;
  } | null;
  service?: { id: string; name: string; category?: string } | null;
}

interface AppointmentActsTraceabilitySectionProps {
  acts: AppointmentActTraceView[];
  loading?: boolean;
  /** Extra classes on the outer card (default includes mt-6). */
  className?: string;
  /** Optional actions in the section header (e.g. admin "Ajouter"). */
  headerActions?: ReactNode;
  /** Optional action slot per act (e.g. admin edit button). */
  renderActActions?: (act: AppointmentActTraceView) => ReactNode;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return {};
}

function str(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR');
}

function DetailItem({ label, value }: { label: string; value?: string | null }) {
  const display = value?.trim() || '—';
  return (
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5 break-words">{display}</p>
    </div>
  );
}

function ActTraceCard({
  act,
  actions,
}: {
  act: AppointmentActTraceView;
  actions?: ReactNode;
}) {
  const [photos, setPhotos] = useState<ActPhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPhotosLoading(true);
        const list = await listActPhotos(act.id);
        if (!cancelled) setPhotos(list);
      } catch {
        if (!cancelled) setPhotos([]);
      } finally {
        if (!cancelled) setPhotosLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [act.id]);

  const demand = asRecord(act.demandDetails);
  const jewelry = {
    ...asRecord(demand.jewelry),
    ...asRecord(act.piercingRecord?.jewelry),
  };
  const placement = {
    ...asRecord(demand.placement),
    ...asRecord(act.piercingRecord?.placement),
  };

  const zoneName =
    act.piercingRecord?.bodyZone?.name ||
    str(placement.area) ||
    str(placement.specific) ||
    '';
  const typeName =
    act.piercingRecord?.piercingTypeModel?.name ||
    act.piercingRecord?.piercingType ||
    str(demand.type) ||
    '';
  const side = str(placement.side);
  const sideLabel =
    side === 'left' ? 'Gauche' : side === 'right' ? 'Droit' : side === 'center' ? 'Centre' : side;

  const jewelryRef = str(jewelry.reference) || str(jewelry.type);
  const batchNumber = str(jewelry.batchNumber);
  const needleGauge =
    str(act.piercingRecord?.needleGauge) || str(jewelry.needleGauge) || str(jewelry.gauge);
  const sterilizationDate = str(jewelry.sterilizationDate);
  const expiryDate = str(jewelry.expiryDate);
  const material = str(jewelry.material);
  const hasManualTrace = !!(batchNumber || jewelryRef || needleGauge || sterilizationDate || expiryDate);

  const title =
    act.serviceName ||
    act.service?.name ||
    (act.sequence != null ? `Acte ${act.sequence}` : `Acte ${act.id.slice(-6)}`);

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            {act.sequence != null ? `Acte ${act.sequence}` : 'Acte'} — {title}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {act.performedAt ? `Réalisé le ${formatDateTime(act.performedAt)}` : 'Non daté'}
            {zoneName ? ` · ${zoneName}` : ''}
            {typeName ? ` · ${typeName}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              actStatusBadgeClasses[act.status] || 'bg-gray-100 text-gray-700'
            }`}
          >
            {actStatusLabels[act.status] || act.status}
          </span>
          {actions}
        </div>
      </div>

      <div className="p-4 space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Stethoscope className="w-4 h-4 text-violet-600" />
            <h3 className="text-sm font-semibold text-gray-800">Détails de l&apos;acte</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <DetailItem label="Zone" value={zoneName} />
            <DetailItem label="Type" value={typeName} />
            <DetailItem label="Côté" value={sideLabel} />
            <DetailItem label="Précision" value={str(placement.specific)} />
            <DetailItem label="Matériau" value={material} />
            {demand.photosCaptureConfirmed != null && (
              <DetailItem
                label="Capture validée"
                value={demand.photosCaptureConfirmed ? 'Oui' : 'Non'}
              />
            )}
          </div>
          {act.notes && (
            <p className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">
              {act.notes}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-gray-800">Traçabilité produit</h3>
          </div>
          {hasManualTrace ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <DetailItem label="Réf. bijou / aiguille" value={jewelryRef} />
              <DetailItem label="Calibre / Gauge" value={needleGauge} />
              <DetailItem label="N° lot / stérilisation" value={batchNumber} />
              <DetailItem label="Date de stérilisation" value={sterilizationDate} />
              <DetailItem label="Utiliser avant" value={expiryDate} />
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">
              Aucune saisie manuelle — vérifier les photos d&apos;étiquettes ci-dessous.
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Camera className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-gray-800">Photos (résultat &amp; traçabilité)</h3>
          </div>
          {photosLoading ? (
            <p className="text-sm text-gray-500">Chargement des photos...</p>
          ) : (
            <ActPhotoGallery photos={photos} />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Read-only panel: acts + product lot fields + session photos for appointment consultation.
 */
export function AppointmentActsTraceabilitySection({
  acts,
  loading = false,
  className = 'mt-6',
  headerActions,
  renderActActions,
}: AppointmentActsTraceabilitySectionProps) {
  return (
    <div className={`card ${className}`.trim()}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-9 h-9 bg-violet-100 rounded-lg">
            <Stethoscope className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-textPrimary">Actes &amp; traçabilité</h2>
            <p className="text-sm text-gray-500">
              {acts.length} acte{acts.length > 1 ? 's' : ''} · photos et lots associés
            </p>
          </div>
        </div>
        {headerActions}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Chargement des actes...</p>
      ) : acts.length === 0 ? (
        <p className="text-sm text-gray-500">Aucun acte associé à ce rendez-vous.</p>
      ) : (
        <div className="space-y-4">
          {acts.map((act) => (
            <ActTraceCard
              key={act.id}
              act={act}
              actions={renderActActions?.(act)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Appointment statuses where the practitioner has started / finished the service. */
export function isAppointmentTreated(status?: string | null): boolean {
  if (!status) return false;
  return ['IN_PROGRESS', 'AWAITING_CHECKOUT', 'COMPLETED'].includes(status);
}
